import { useState, useEffect, useCallback } from 'react'

import { CookieSettingsType, CookieConsentType, ACCEPT_ALL_SETTINGS, REJECT_ALL_SETTINGS } from '../types/cookie'
import { getConsent } from '../utils/consent-checker'

const COOKIE_CONSENT_KEY = 'cookie-consent'

export const useCookieConsent = () => {
  const [consent, setConsent] = useState<CookieConsentType | null>(null)
  const [showBanner, setShowBanner] = useState(false)
  const [showSettings, setShowSettings] = useState(false)

  // Save consent to localStorage
  const saveConsent = useCallback((settings: CookieSettingsType) => {
    const newConsent: CookieConsentType = {
      isSet: true,
      settings: {
        ...settings,
        strictlyNecessary: true, // Always enforce strictly necessary cookies
      },
      timestamp: Date.now(),
    }

    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(newConsent))
    setConsent(newConsent)
    setShowBanner(false)
    setShowSettings(false)
  }, [])

  // Accept all cookies
  const acceptAll = useCallback(() => {
    saveConsent(ACCEPT_ALL_SETTINGS)
  }, [saveConsent])

  // Reject all non-essential cookies
  const rejectAll = useCallback(() => {
    saveConsent(REJECT_ALL_SETTINGS)
  }, [saveConsent])

  // Save custom settings
  const saveCustomSettings = useCallback(
    (settings: CookieSettingsType) => {
      saveConsent(settings) // strictlyNecessary enforcement moved to saveConsent
    },
    [saveConsent],
  )

  // Close banner
  const closeBanner = useCallback(() => {
    setShowBanner(false)
  }, [])

  // Open settings modal
  const openSettings = useCallback(() => {
    setShowSettings(true)
  }, [])

  // Close settings modal
  const closeSettings = useCallback(() => {
    setShowSettings(false)
  }, [])

  // Load consent from localStorage
  useEffect(() => {
    const storedConsent = getConsent()
    if (storedConsent !== null) {
      try {
        // 저장된 동의 정보가 유효한지 확인
        if (storedConsent.isSet && storedConsent.settings && storedConsent.timestamp) {
          setConsent(storedConsent)
          setShowBanner(false)
        } else {
          // 유효하지 않은 데이터인 경우 삭제하고 배너 표시
          localStorage.removeItem(COOKIE_CONSENT_KEY)
          setShowBanner(true)
        }
      } catch (error) {
        console.error('Failed to parse stored cookie consent:', error)
        // 잘못된 JSON 데이터 삭제
        localStorage.removeItem(COOKIE_CONSENT_KEY)
        setShowBanner(true)
      }
    } else {
      setShowBanner(true)
    }
  }, [])

  // Helper functions to check specific cookie types
  const hasPerformanceConsent = consent?.settings.performance ?? false
  const hasFunctionalConsent = consent?.settings.functional ?? false
  const hasTargetingConsent = consent?.settings.targeting ?? false
  const hasStrictlyNecessaryConsent = true // Always true

  return {
    consent,
    showBanner,
    showSettings,
    acceptAll,
    rejectAll,
    saveCustomSettings,
    closeBanner,
    openSettings,
    closeSettings,
    hasPerformanceConsent,
    hasFunctionalConsent,
    hasTargetingConsent,
    hasStrictlyNecessaryConsent,
  }
}
